import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'

import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

export interface RefundSegment {
    /** Boundary shown at the start of this segment, e.g. "60 days before". */
    startLabel: string
    /** What comes back, e.g. "50% back" or "Non refundable". */
    valueLabel: string
    /** The whole window spelled out, e.g. "60–45 days before". Used by the stacked phone layout. */
    rangeLabel: string
    /** Charge severity, which drives the segment colour. */
    severity: 'low' | 'mid' | 'high'
}

interface RefundTimelineProps {
    cancelBetweenLabel: string
    amountRefundableLabel: string
    /** One-line summary of the best case, e.g. "Cancel by 60 days before for 50% back". */
    headline: string
    segments: RefundSegment[]
    isMobile: boolean
}

const SEVERITY = {
    low: { bar: 'success.main', text: 'success.main' },
    mid: { bar: 'warning.main', text: 'textColors.primary' },
    high: { bar: 'error.main', text: 'error.main' }
} as const

const RefundTimeline: React.FC<RefundTimelineProps> = ({
    cancelBetweenLabel,
    amountRefundableLabel,
    headline,
    segments,
    isMobile
}) => {
    const labelSx = {
        fontSize: isMobile ? '0.688rem' : '0.75rem',
        color: 'textColors.subheading',
        fontWeight: 600
    }

    return (
        <Stack
            spacing={isMobile ? 1.5 : 2}
            sx={{
                p: isMobile ? 1.75 : 2.5,
                borderRadius: HOLIDAYS_RADIUS.NESTED,
                border: '1px solid',
                borderColor: 'borders.default',
                bgcolor: 'neutral.100'
            }}
        >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <EventAvailableOutlinedIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
                <Typography
                    sx={{
                        fontSize: isMobile ? '1rem' : '0.938rem',
                        fontWeight: 700,
                        color: 'textColors.primary'
                    }}
                >
                    {headline}
                </Typography>
            </Stack>

            {isMobile ? (
                /*
                 * One row per window: a colour chip, the window in full, the amount. A phone has no
                 * room for a segmented track without truncating the very labels that carry the rule.
                 */
                <Stack spacing={1.25}>
                    <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                        <Typography sx={labelSx}>{cancelBetweenLabel}</Typography>
                        <Typography sx={labelSx}>{amountRefundableLabel}</Typography>
                    </Stack>

                    {segments.map(segment => (
                        <Stack key={segment.startLabel} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                            <Box
                                sx={{
                                    width: 6,
                                    alignSelf: 'stretch',
                                    minHeight: 34,
                                    borderRadius: 50,
                                    flexShrink: 0,
                                    bgcolor: SEVERITY[segment.severity].bar
                                }}
                            />

                            <Typography
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: 'textColors.primary'
                                }}
                            >
                                {segment.rangeLabel}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    textAlign: 'end',
                                    flexShrink: 0,
                                    color: SEVERITY[segment.severity].text
                                }}
                            >
                                {segment.valueLabel}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <Box>
                    <Typography sx={{ ...labelSx, mb: 0.75 }}>{cancelBetweenLabel}</Typography>

                    {/*
                     * Each column owns one segment: its start boundary, its slice of the track and
                     * its value, so the three rows stay aligned however the labels wrap. There is no
                     * closing boundary — every schedule runs to departure, so a "Departure" tick
                     * would only name the point the track already ends at.
                     */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {segments.map(segment => (
                            <Stack key={segment.startLabel} spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        color: 'textColors.subtitle',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {segment.startLabel}
                                </Typography>

                                <Box sx={{ height: 12, borderRadius: 50, bgcolor: SEVERITY[segment.severity].bar }} />

                                <Typography
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                        color: SEVERITY[segment.severity].text
                                    }}
                                >
                                    {segment.valueLabel}
                                </Typography>
                            </Stack>
                        ))}
                    </Box>

                    <Typography sx={{ ...labelSx, mt: 0.75 }}>{amountRefundableLabel}</Typography>
                </Box>
            )}
        </Stack>
    )
}

export { RefundTimeline }
export default RefundTimeline
